import { delayedPromise , WindowHelperService } from '@anecdotes/utils';
import {
  ChangeDetectorRef,
  DestroyRef,
  Directive,
  ElementRef,
  Input,
  Optional,
  SimpleChanges,
  inject,
  Inject,
} from '@angular/core';
import { NgbPopover, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { Subject, fromEvent } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IS_MOBILE_TOKEN } from '@anecdotes/design-system/injection-tokens';

export class ClassIdProvider {
  private static tooltipIndex = 0;
  private static popoverIndex = 0;

  getNextTooltipClassId(): string {
    const classId = `x-tooltip-${ClassIdProvider.tooltipIndex}`;
    ClassIdProvider.tooltipIndex++;
    return classId;
  }

  getNextPopoverClassId(): string {
    const classId = `x-popover-${ClassIdProvider.popoverIndex}`;
    ClassIdProvider.popoverIndex++;
    return classId;
  }
}

@Directive({
  selector: '[keepOnHover]',
})
export class KeepTooltipOrPopoverOnHoverDirective {
  private static delay = 100;

  private destroyRef = inject(DestroyRef);
  private isHostHovered: boolean;
  private host: HTMLElement;
  private classId: string;
  private readonly detacher = new Subject<void>();
  private tooltipDetacher = new Subject<void>();
  private tooltipOrPopover: NgbTooltip | NgbPopover;
  private isTooltipOrPopoverHovered: boolean;
  private forceClose = false;

  private get hasClassId(): boolean {
    if (this.tooltipOrPopover instanceof NgbTooltip) {
      return this.tooltipOrPopover.tooltipClass.includes(this.classId);
    }

    if (this.tooltipOrPopover instanceof NgbPopover) {
      return this.tooltipOrPopover.popoverClass.includes(this.classId);
    }
  }

  @Input()
  keepOnHover: boolean;

  @Input()
  closeOnClick = false;

  constructor(
    @Optional() private classIdProvider: ClassIdProvider,
    @Optional() tooltip: NgbTooltip,
    @Optional() popover: NgbPopover,
    htmlRef: ElementRef,
    private readonly windowHelper: WindowHelperService,
    private readonly cd: ChangeDetectorRef,
    @Inject(IS_MOBILE_TOKEN) private readonly isMobile: boolean,
  ) {
    if (!this.classIdProvider) {
      // This workaround was made for testablity
      this.classIdProvider = new ClassIdProvider();
    }

    if (tooltip) {
      this.tooltipOrPopover = tooltip;
    } else if (popover) {
      this.tooltipOrPopover = popover;
    }

    if (this.tooltipOrPopover) {
      this.tooltipOrPopover.triggers = this.isMobile ? 'click' : null;
      this.tooltipOrPopover.autoClose = this.isMobile;
    }

    this.host = htmlRef.nativeElement;
  }

  ngOnInit(): void {
    this.initAsync();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.isMobile && 'keepOnHover' in changes) {
      this.destroyAllSubscriptions();

      fromEvent(this.host, 'mouseenter')
        .pipe(takeUntil(this.detacher), takeUntilDestroyed(this.destroyRef))
        .subscribe(this.hostMouseEnter.bind(this));

      fromEvent(this.host, 'mouseleave')
        .pipe(takeUntil(this.detacher), takeUntilDestroyed(this.destroyRef))
        .subscribe(this.hostMouseLeave.bind(this));
    }
  }

  ngOnDestroy(): void {
    this.destroyAllSubscriptions();
  }

  private async initAsync(): Promise<void> {
    // This is a workaround as we have to delay the initialization
    await delayedPromise(0);

    this.generateClassId();
    this.addIdentifierClass();

    this.cd.detectChanges();
  }

  private async hostMouseEnter(): Promise<void> {
    if (!this.tooltipOrPopover.isOpen()) {
      /*
       * If the tooltipClass value is re-initialized,
       * the ngbTooltip directive resets its classes and removes the classId.
       * In this case, the identifier class must be added again to have access to the tooltipOrPopoverWindow.
       */
      if (!this.hasClassId) {
        this.addIdentifierClass();
      }

      this.tooltipOrPopover.open();

      const tooltipOrPopoverWindow = this.windowHelper.getWindow().document.querySelector(`.${this.classId}`);

      if (tooltipOrPopoverWindow && this.keepOnHover) {
        fromEvent(tooltipOrPopoverWindow, 'mouseenter')
          .pipe(takeUntil(this.tooltipDetacher), takeUntilDestroyed(this.destroyRef))
          .subscribe(this.tooltipOrPopoverMouseEnter.bind(this));

        fromEvent(tooltipOrPopoverWindow, 'mouseleave')
          .pipe(takeUntil(this.tooltipDetacher), takeUntilDestroyed(this.destroyRef))
          .subscribe(this.tooltipOrPopoverMouseLeave.bind(this));

        fromEvent(tooltipOrPopoverWindow, 'click')
          .pipe(filter(() => this.closeOnClick), takeUntil(this.tooltipDetacher), takeUntilDestroyed(this.destroyRef))
          .subscribe(this.tooltipOrPopoverMouseClick.bind(this));
      }
    }

    this.isHostHovered = true;
  }

  private async hostMouseLeave(): Promise<void> {
    this.isHostHovered = false;

    if (!this.keepOnHover) {
      this.closeToolTipOrPopover();
      return;
    }

    await delayedPromise(KeepTooltipOrPopoverOnHoverDirective.delay);
    this.closeToolTipOrPopover();
  }

  private tooltipOrPopoverMouseEnter(): void {
    this.isTooltipOrPopoverHovered = true;
  }

  private tooltipOrPopoverMouseClick(): void {
    if (this.closeOnClick) {
      this.forceClose = true;
    }
    try {
      this.closeToolTipOrPopover();
    } finally {
      this.forceClose = false;
    }
  }

  private async tooltipOrPopoverMouseLeave(): Promise<void> {
    this.isTooltipOrPopoverHovered = false;
    await delayedPromise(KeepTooltipOrPopoverOnHoverDirective.delay);
    this.closeToolTipOrPopover();
  }

  private closeToolTipOrPopover(): void {
    if ((!this.isTooltipOrPopoverHovered && !this.isHostHovered) || this.forceClose) {
      this.tooltipOrPopover.close();
      this.tooltipDetacher.next();
    }
  }

  private destroyAllSubscriptions(): void {
    this.detacher.next();
    this.tooltipDetacher.next();
  }

  private concatClasses(classes: string, classToAdd: string): string {
    if (classes) {
      return ` ${classToAdd}`;
    }

    return classToAdd;
  }

  private generateClassId(): void {
    if (this.tooltipOrPopover instanceof NgbTooltip) {
      this.classId = this.classIdProvider.getNextTooltipClassId();
      return;
    }

    if (this.tooltipOrPopover instanceof NgbPopover) {
      this.classId = this.classIdProvider.getNextPopoverClassId();
    }
  }

  private addIdentifierClass(): void {
    if (this.tooltipOrPopover instanceof NgbTooltip) {
      this.tooltipOrPopover.tooltipClass += this.concatClasses(this.tooltipOrPopover.tooltipClass, this.classId);
      return;
    }

    if (this.tooltipOrPopover instanceof NgbPopover) {
      this.tooltipOrPopover.popoverClass += this.concatClasses(this.tooltipOrPopover.popoverClass, this.classId);
    }
  }
}
